import { useEffect } from 'react';
import { Info, Calculator } from 'lucide-react';
import type { RowSpacingPattern } from '../../types/spray';
import sprayService from '../../services/spray';
import { formatAmount } from '@/utils/sprayDose';
import type { StripCoverage } from '@/utils/weedStrip';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';

interface PartialAreaConfigProps {
  rowPattern: RowSpacingPattern;
  coveragePct: number;
  concentrationFactor: number;
  ratePerHa?: number | null;
  rateUnit?: string;
  outputRateLpha?: number | null;
  totalTargetAreaHa?: number;
  whpDays?: number | null;
  scheduledDate?: string;
  /** Set when the sprayer wets a weed strip: its share of the targets. */
  stripCoverage?: StripCoverage | null;
  /** A weed-strip spray whose share could not be worked out, and nobody has
   *  typed one yet: the coverage reads blank rather than a false 100%. */
  coverageUnset?: boolean;
  onRowPatternChange: (pattern: RowSpacingPattern) => void;
  onCoverageChange: (pct: number) => void;
  onConcentrationChange: (factor: number) => void;
}

const CONCENTRATION_PRESETS = [
  { label: '½×', value: 0.5 },
  { label: '¾×', value: 0.75 },
  { label: '1× (as typed)', value: 1.0 },
  { label: '1¼×', value: 1.25 },
  { label: '1½×', value: 1.5 },
  { label: '2×', value: 2.0 },
];

const ROW_PATTERN_OPTIONS: { value: RowSpacingPattern; label: string; auto?: number }[] = [
  { value: 'every_row',         label: 'Every row (100%)',            auto: 100  },
  { value: 'every_second_row',  label: 'Every second row (50%)',      auto: 50   },
  { value: 'every_third_row',   label: 'Every third row (33%)',       auto: 33.33 },
  { value: 'custom',            label: 'Custom / partial area',       auto: undefined },
  { value: 'weed_strip',        label: 'Weed strip only (from the sprayer)', auto: undefined },
];

function PartialAreaConfig({
  rowPattern,
  coveragePct,
  concentrationFactor,
  ratePerHa,
  rateUnit = 'L/ha',
  outputRateLpha,
  totalTargetAreaHa = 0,
  whpDays,
  scheduledDate,
  stripCoverage = null,
  coverageUnset = false,
  onRowPatternChange,
  onCoverageChange,
  onConcentrationChange,
}: PartialAreaConfigProps) {
  const timezone = useOrgTimezone();
  // Auto-set coverage when row pattern changes (if not custom)
  useEffect(() => {
    const defaultPct = sprayService.defaultCoverageForPattern(rowPattern);
    if (defaultPct !== null) {
      onCoverageChange(defaultPct);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowPattern]);

  const { effectiveAreaHa, estimatedProduct: estimatedChemicalL, dilute, needsOutputRate } =
    sprayService.estimateSprayVolumes({
      totalAreaHa: totalTargetAreaHa,
      coveragePct,
      rate: ratePerHa || 0,
      rateUnit,
      concentrationFactor,
      outputRateLpha,
    });

  const earliestHarvest = scheduledDate && whpDays
    ? sprayService.earliestHarvestDate(scheduledDate, whpDays)
    : null;

  const isStrip = rowPattern === 'weed_strip';
  const stripPct = isStrip ? stripCoverage?.coveragePct ?? null : null;
  const isAutoSet = rowPattern !== 'custom' && (!isStrip || stripPct != null);
  // The strip pattern means nothing without a strip sprayer, so it is only
  // offered with one, or on a spray already saved with it.
  const patternOptions = ROW_PATTERN_OPTIONS.filter(
    opt => opt.value !== 'weed_strip' || isStrip || !!stripCoverage,
  );

  return (
    <div className="space-y-4">
      {/* Row spacing pattern */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1.5">
          Row spray pattern
        </label>
        <select
          value={rowPattern}
          onChange={e => onRowPatternChange(e.target.value as RowSpacingPattern)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {patternOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Coverage percentage */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-neutral-700">
            Area coverage
          </label>
          {isAutoSet && (
            <span className="text-xs text-neutral-400 flex items-center gap-1">
              <Info className="h-3.5 w-3.5" />
              {isStrip ? 'Worked out from the strip' : 'Auto-set from row pattern'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="1"
            max="100"
            step="0.5"
            value={coveragePct}
            onChange={e => onCoverageChange(parseFloat(e.target.value))}
            className="flex-1 accent-primary-600"
          />
          <div className="flex items-center gap-1 min-w-[80px]">
            <input
              type="number"
              min="0.1"
              max="100"
              step="0.5"
              aria-label="Area coverage %"
              placeholder="%"
              value={coverageUnset ? '' : coveragePct}
              onChange={e => onCoverageChange(Math.min(100, Math.max(0.1, parseFloat(e.target.value) || 0)))}
              className="w-16 border border-neutral-300 rounded px-2 py-1 text-sm text-right"
            />
            <span className="text-sm text-neutral-500">%</span>
          </div>
        </div>
        {isStrip && stripCoverage && (
          <p className="text-xs text-neutral-600 mt-1 flex items-start gap-1">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            {stripPct != null && stripCoverage.sprayedHa != null ? (
              <span>
                The sprayer wets {stripCoverage.stripWidthM} m per pass, so it sprays{' '}
                {stripCoverage.sprayedHa.toFixed(2)} ha of the {stripCoverage.totalHa.toFixed(2)} ha
                selected ({stripPct}%). Its calibrated L/ha is per sprayed hectare, and so is the label rate.
              </span>
            ) : stripCoverage.missingRowSpacing.length ? (
              <span className="text-amber-700">
                No row spacing is recorded for {stripCoverage.missingRowSpacing.join(', ')}, so
                enter the % of the area the strip sprays: the strip width over the row spacing.
                A {stripCoverage.stripWidthM} m strip on 4 m rows is{' '}
                {Math.round((stripCoverage.stripWidthM / 4) * 1000) / 10}%.
              </span>
            ) : (
              <span>Choose the blocks to see the strip's share of them.</span>
            )}
          </p>
        )}
        {isStrip && !stripCoverage && (
          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
            <Info className="h-3.5 w-3.5" />
            The % of each block's ground the weed strip covers: its width over the row spacing.
          </p>
        )}
        {rowPattern === 'custom' && (
          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
            <Info className="h-3.5 w-3.5" />
            Enter the % of the target area geometry that will receive product (e.g. 20% for under-tree herbicide strip)
          </p>
        )}
      </div>

      {/* Concentration factor */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1.5">
          Concentration (rate multiplier)
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {CONCENTRATION_PRESETS.map(preset => (
            <button
              key={preset.value}
              type="button"
              onClick={() => onConcentrationChange(preset.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                Math.abs(concentrationFactor - preset.value) < 0.001
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-neutral-600 border-neutral-300 hover:border-primary-400'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0.01"
            max="10"
            step="0.05"
            value={concentrationFactor}
            onChange={e => onConcentrationChange(Math.max(0.01, parseFloat(e.target.value) || 0))}
            className="w-24 border border-neutral-300 rounded-lg px-3 py-2 text-sm"
          />
          <span className="text-sm text-neutral-500">× field rate</span>
        </div>
        {concentrationFactor !== 1.0 && (
          <p className="text-xs text-neutral-500 mt-1">
            {concentrationFactor < 1
              ? `${(concentrationFactor * 100).toFixed(0)}% of the field rate — ensure this is appropriate for the situation`
              : `${(concentrationFactor * 100).toFixed(0)}% of the field rate — check label for maximum permitted rate`
            }
          </p>
        )}
      </div>

      {/* Live calculation preview */}
      {ratePerHa && totalTargetAreaHa > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2 text-primary-700 font-medium text-sm mb-2">
            <Calculator className="h-4 w-4" />
            Spray preview
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-neutral-500 text-xs">Total target area</p>
              <p className="font-semibold text-neutral-800">{totalTargetAreaHa.toFixed(2)} ha</p>
            </div>
            <div>
              <p className="text-neutral-500 text-xs">Effective spray area</p>
              <p className="font-semibold text-primary-700">{effectiveAreaHa.toFixed(2)} ha</p>
            </div>
            {estimatedChemicalL > 0 && (
              <>
                <div>
                  <p className="text-neutral-500 text-xs">Est. chemical needed</p>
                  <p className="font-semibold text-neutral-800">
                    {formatAmount(estimatedChemicalL, (rateUnit || 'L/ha').split('/')[0])}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-500 text-xs">Actual rate applied</p>
                  <p className="font-semibold text-neutral-800">
                    {(ratePerHa * concentrationFactor).toFixed(2)} {rateUnit}
                  </p>
                </div>
              </>
            )}
          </div>
          {dilute && needsOutputRate && (
            <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
              <Info className="h-3.5 w-3.5 shrink-0" />
              This is a per-100L (dilute) rate — set the sprayer's output rate (L/ha) to estimate product volume.
            </p>
          )}
        </div>
      )}

      {/* WHP reminder */}
      {earliestHarvest && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
          <p className="font-medium text-amber-800">Withholding period reminder</p>
          <p className="text-amber-700 mt-0.5">
            {whpDays}-day WHP — earliest safe harvest date:{' '}
            <strong>{formatLocalDateInTz(earliestHarvest, timezone)}</strong>
          </p>
        </div>
      )}
    </div>
  );
}

export default PartialAreaConfig;
